/**
 * Production-safe logging utility
 * Prefer this over direct console.* in new code.
 *
 * Production behavior:
 * - debug/info: silenciados (também removidos do bundle pelo esbuild via vite.config.ts)
 * - warn/error: preservados, mas SEMPRE sanitize PII antes de logar usando os helpers `mask*`
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

interface LogContext {
  userId?: string;
  component?: string;
  action?: string;
  isLoading?: boolean;
  isAdmin?: boolean;
  metadata?: Record<string, unknown>;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private minLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN;

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const levelName = LogLevel[level];
    const prefix = `[${timestamp}] ${levelName}`;

    if (context) {
      const contextStr = Object.entries(context)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => `${key}=${JSON.stringify(value)}`)
        .join(' ');
      return `${prefix} ${message} ${contextStr}`;
    }

    return `${prefix} ${message}`;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.minLevel;
  }

  debug(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.debug(this.formatMessage(LogLevel.DEBUG, message, context));
    }
  }

  info(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(this.formatMessage(LogLevel.INFO, message, context));
    }
  }

  warn(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage(LogLevel.WARN, message, context));
    }
  }

  error(message: string, error?: Error, context?: LogContext): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const errorContext = error ? { ...context, error: error.message, stack: error.stack } : context;
      console.error(this.formatMessage(LogLevel.ERROR, message, errorContext));
    }
  }

  // Performance logging for development
  time(label: string): void {
    if (this.isDevelopment) {
      console.time(label);
    }
  }

  timeEnd(label: string): void {
    if (this.isDevelopment) {
      console.timeEnd(label);
    }
  }
}

export const logger = new Logger();

// Convenience exports for common use cases
export const logInfo = (message: string, context?: LogContext) => logger.info(message, context);
export const logWarn = (message: string, context?: LogContext) => logger.warn(message, context);
export const logError = (message: string, error?: Error, context?: LogContext) => logger.error(message, error, context);
export const logDebug = (message: string, context?: LogContext) => logger.debug(message, context);

/* ============================================================
 * PII / sensitive data masking helpers
 * Use these BEFORE logging anything that may contain user data.
 * ============================================================ */

export const maskEmail = (email?: string | null): string => {
  if (!email || typeof email !== 'string') return '';
  const [local, domain] = email.split('@');
  if (!domain) return '***';
  const visible = local.slice(0, 1);
  return `${visible}***@${domain}`;
};

export const maskCpf = (cpf?: string | null): string => {
  if (!cpf || typeof cpf !== 'string') return '';
  const digits = cpf.replace(/\D/g, '');
  if (digits.length < 4) return '***';
  // CPF (11) ou CNPJ (14) — preserva últimos 2
  return `***.***.***-${digits.slice(-2)}`;
};

export const maskPhone = (phone?: string | null): string => {
  if (!phone || typeof phone !== 'string') return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return '***';
  return `(${digits.slice(0, 2)}) ****-${digits.slice(-4)}`;
};

export const maskToken = (token?: string | null): string => {
  if (!token || typeof token !== 'string') return '';
  if (token.length <= 8) return '***';
  return `${token.slice(0, 4)}***${token.slice(-2)}`;
};

export const maskId = (id?: string | null): string => {
  if (!id || typeof id !== 'string') return '';
  if (id.length <= 8) return '***';
  return `${id.slice(0, 8)}***`;
};

/**
 * Sanitiza um objeto removendo/mascarando campos PII conhecidos.
 * Útil quando precisar logar uma estrutura inteira (e.g. resposta de API).
 */
export const sanitizePII = <T extends Record<string, unknown>>(obj: T | null | undefined): Partial<T> => {
  if (!obj || typeof obj !== 'object') return {};
  const PII_KEYS = new Set([
    'email', 'e_mail', 'mail',
    'cpf', 'cnpj', 'document', 'document_number', 'tax_id',
    'phone', 'telefone', 'celular', 'mobile',
    'password', 'senha', 'token', 'access_token', 'refresh_token', 'api_key', 'secret',
    'card_number', 'card', 'cvv', 'card_encrypted',
    'address', 'endereco', 'cep',
  ]);
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    const lower = k.toLowerCase();
    if (PII_KEYS.has(lower)) {
      if (lower.includes('email') || lower === 'mail') out[k] = maskEmail(String(v ?? ''));
      else if (lower.includes('cpf') || lower.includes('cnpj') || lower.includes('document') || lower.includes('tax_id')) out[k] = maskCpf(String(v ?? ''));
      else if (lower.includes('phone') || lower.includes('telefone') || lower.includes('celular') || lower.includes('mobile')) out[k] = maskPhone(String(v ?? ''));
      else if (lower.includes('token') || lower.includes('secret') || lower.includes('key') || lower.includes('password') || lower.includes('senha')) out[k] = maskToken(String(v ?? ''));
      else out[k] = '***';
    } else {
      out[k] = v;
    }
  }
  return out as Partial<T>;
};
